import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useReferralCode, useReferralStats } from "@/hooks/useReferrals";
import { ReferralShareButtons } from "./ReferralShareButtons";
import { ReferralEmailModal } from "./ReferralEmailModal";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePlan } from "@/hooks/usePlan";

interface ReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReferralModal({ open, onOpenChange }: ReferralModalProps) {
  const { data: codeData, isLoading } = useReferralCode();
  const stats = useReferralStats();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { nurseryName } = usePlan();
  const [emailOpen, setEmailOpen] = useState(false);

  const referralUrl = codeData ? `https://nursly.co.uk/signup?ref=${codeData.code}` : "";
  const isAdmin = role === "admin" || role === "super_admin";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl text-nursly-forest">
              Know a nursery still running on paper?
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm font-body font-light text-nursly-muted">
            Share Nursly with another nursery manager. When they start a Professional plan, you both get two months free. No limit on how many nurseries you can refer.
          </p>
          <p className="text-[13px] font-body text-nursly-muted">
            Your referral credits are applied automatically to your next bill. No voucher codes. No chasing.
          </p>


          {/* How it works */}
          <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "rgba(13,31,20,0.08)", background: "#F9F6F1" }}>
            <p className="text-[11px] font-body font-semibold uppercase tracking-wider text-nursly-faint">How it works</p>
            <ol className="space-y-1.5 text-[13px] font-body text-nursly-ink">
              <li className="flex gap-2"><span className="text-nursly-sage font-semibold">1.</span> Share your unique referral link</li>
              <li className="flex gap-2"><span className="text-nursly-sage font-semibold">2.</span> They sign up and start a Professional plan</li>
              <li className="flex gap-2"><span className="text-nursly-sage font-semibold">3.</span> Both nurseries get 2 months free, applied automatically</li>
            </ol>
            <p className="text-[12px] font-body font-medium text-nursly-amber pt-1" style={{ color: "#B45309" }}>
              Reward activates when they start a paid plan, not on free signup.
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <ReferralShareButtons
              referralUrl={referralUrl}
              onEmailClick={() => setEmailOpen(true)}
              compact
            />
          )}

          {!isAdmin && (
            <p className="text-[12px] font-body text-nursly-muted border-t pt-3" style={{ borderColor: "rgba(13,31,20,0.08)" }}>
              Your referral link is unique to {nurseryName || "your nursery"}. Any reward earned goes to your nursery's account and will be applied by your Owner.
            </p>
          )}

          {/* Stats row: Admins and Owners only */}
          {isAdmin && (
            <div className="border-t pt-3 mt-1" style={{ borderColor: "rgba(13,31,20,0.08)" }}>
              <p className="text-[13px] font-body text-nursly-muted">
                <span className="font-medium text-nursly-ink">{stats.referred}</span> referred
                <span className="mx-1.5">·</span>
                <span className="font-medium text-nursly-ink">{stats.converted}</span> converted
                <span className="mx-1.5">·</span>
                <span className="font-medium text-nursly-ink">{stats.monthsEarned}</span> months earned
              </p>
              <button
                onClick={() => { onOpenChange(false); navigate("/settings?tab=referrals"); }}
                className="text-xs font-body text-nursly-sage hover:underline mt-1"
              >
                View full referral history →
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReferralEmailModal
        open={emailOpen}
        onOpenChange={setEmailOpen}
        referralUrl={referralUrl}
        referralCode={codeData?.code || ""}
      />
    </>
  );
}
